import { FC, PropsWithChildren } from 'react'
import styles from './seo-text.module.scss'
import classNames from '@/utils/helpers/class-names.helper'

type SeoTextProps = {
  className?: string
  dangerouslySetInnerHTML?: { __html: string }
}

type SeoTextSection = {
  heading?: string
  headingAs?: 'h1' | 'h2' | 'div'
  withoutColumns?: boolean
  className?: string
}

type SeoTextColumnProps = {}

type SeoTextSubTitleProps = {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'p'
  dangerouslySetInnerHTML?: { __html: string }
}

interface ISeoText extends FC<PropsWithChildren<SeoTextProps>> {
  Section: FC<PropsWithChildren<SeoTextSection>>
  Column: FC<PropsWithChildren<SeoTextColumnProps>>
  SubTitle: FC<PropsWithChildren<SeoTextSubTitleProps>>
}

const SeoText: ISeoText = ({ children, className, dangerouslySetInnerHTML }) => {
  if (dangerouslySetInnerHTML) {
    return (
      <div
        className={classNames(styles.content, className)}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      />
    )
  }

  return <div className={classNames(styles.content, className)}>{children}</div>
}

const Section: ISeoText['Section'] = ({
  children,
  heading,
  headingAs = 'div',
  withoutColumns,
  className,
}) => {
  const headingBlok =
    heading && headingAs === 'h1' ? (
      <h1 className={classNames(styles.heading, 'heading', '_main')}>{heading}</h1>
    ) : heading && headingAs === 'h2' ? (
      <h2 className={classNames(styles.heading, 'heading', '_main')}>{heading}</h2>
    ) : heading && headingAs === 'div' ? (
      <div className={classNames(styles.heading, 'heading', '_main')}>{heading}</div>
    ) : null

  const columns = withoutColumns ? children : <div className={styles.inner}>{children}</div>

  return (
    <section className={classNames(styles.section, className)}>
      <div className="container">
        {headingBlok}
        {columns}
      </div>
    </section>
  )
}

const Column: ISeoText['Column'] = ({ children }) => {
  return <div className={styles.column}>{children}</div>
}

const SubTitle: ISeoText['SubTitle'] = ({ children, as = 'p', dangerouslySetInnerHTML }) => {
  switch (as) {
    case 'h1':
      return dangerouslySetInnerHTML ? (
        <h1 className={styles['sub-title']} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      ) : (
        <h1 className={styles['sub-title']}>{children}</h1>
      )
    case 'h2':
      return dangerouslySetInnerHTML ? (
        <h2 className={styles['sub-title']} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      ) : (
        <h2 className={styles['sub-title']}>{children}</h2>
      )
    case 'h3':
      return dangerouslySetInnerHTML ? (
        <h3 className={styles['sub-title']} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      ) : (
        <h3 className={styles['sub-title']}>{children}</h3>
      )
    case 'h4':
      return dangerouslySetInnerHTML ? (
        <h4 className={styles['sub-title']} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      ) : (
        <h4 className={styles['sub-title']}>{children}</h4>
      )
    case 'h5':
      return dangerouslySetInnerHTML ? (
        <h5 className={styles['sub-title']} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      ) : (
        <h5 className={styles['sub-title']}>{children}</h5>
      )
    case 'h6':
      return dangerouslySetInnerHTML ? (
        <h6 className={styles['sub-title']} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      ) : (
        <h6 className={styles['sub-title']}>{children}</h6>
      )
    case 'div':
      return dangerouslySetInnerHTML ? (
        <div className={styles['sub-title']} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      ) : (
        <div className={styles['sub-title']}>{children}</div>
      )
    default:
      return dangerouslySetInnerHTML ? (
        <p className={styles['sub-title']} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      ) : (
        <p className={styles['sub-title']}>{children}</p>
      )
  }
}

SeoText.Section = Section
SeoText.Column = Column
SeoText.SubTitle = SubTitle

export default SeoText
